import type { Mm, Rotation } from './units.js';
import { normaliseRotation } from './units.js';
import { fail } from './errors.js';

/** SPECIFICATION.md clause 3 — the fixed five-unit catalogue. */
export interface BrickSpec {
  readonly key: BrickKind;
  readonly name: string;
  readonly width: Mm;
  readonly height: Mm;
  /** Single-digit keyboard shortcut, consumed by the app layer. */
  readonly shortcut: string;
}

export type BrickKind =
  | 'stretcher'
  | 'header'
  | 'queenie'
  | 'three_quarter_bat'
  | 'block';

/**
 * Declared order is the order of the tool panel. The quarter bat's key is
 * `queenie` — a historical name that must be kept so existing project files
 * import (clause 3).
 */
export const BRICK_SPECS: Readonly<Record<BrickKind, BrickSpec>> = Object.freeze({
  stretcher: { key: 'stretcher', name: 'Stretcher', width: 215.0, height: 65.0, shortcut: '1' },
  header: { key: 'header', name: 'Header', width: 102.5, height: 65.0, shortcut: '2' },
  queenie: { key: 'queenie', name: 'Quarter bat', width: 46.25, height: 65.0, shortcut: '3' },
  three_quarter_bat: {
    key: 'three_quarter_bat',
    name: 'Three-quarter bat',
    width: 158.75,
    height: 65.0,
    shortcut: '4',
  },
  block: { key: 'block', name: 'Block', width: 440.0, height: 215.0, shortcut: '5' },
} as const);

export const BRICK_KINDS = Object.keys(BRICK_SPECS) as readonly BrickKind[];

export function isBrickKind(value: unknown): value is BrickKind {
  return typeof value === 'string' && Object.hasOwn(BRICK_SPECS, value);
}

export function brickSpec(kind: string): BrickSpec {
  if (!isBrickKind(kind)) fail(`Unknown brick type: ${kind}`);
  return BRICK_SPECS[kind];
}

/** Footprint at the given orientation; at 90 degrees width and height swap. */
export function effectiveSize(kind: BrickKind, rotation: number = 0): readonly [Mm, Mm] {
  const spec = BRICK_SPECS[kind];
  return normaliseRotation(rotation) === 90 ? [spec.height, spec.width] : [spec.width, spec.height];
}

export type { Rotation };
